import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AuthContext } from '../contexts/AuthContext';
import { AmberButton } from '../components/design';
import api from '../utils/api';

const MONO = "'IBM Plex Mono', monospace";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;

    const verifyPayment = async () => {
      hasVerified.current = true;
      try {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) { setError('No session ID provided'); setLoading(false); return; }

        const response = await api.get(`/api/payments/verify-session/${sessionId}`);
        if (response.data.success) {
          setSubscription(response.data.subscription);
          if (typeof refreshUser === 'function') {
            try { await refreshUser(); } catch (_) {}
          }
        } else {
          setError(response.data.message || 'Unable to verify payment. Please contact support.');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('An error occurred while verifying your payment. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#0a1326', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          <CircularProgress size={48} sx={{ color: '#2dd4bf' }} />
          <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd', letterSpacing: '.08em' }}>
            Confirming your subscription…
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#0a1326', display: 'flex', alignItems: 'center', justifyContent: 'center', px: '28px' }}>
        <Box sx={{ maxWidth: '520px', width: '100%', textAlign: 'center' }}>
          <Box sx={{ width: '56px', height: '56px', borderRadius: '14px', background: 'rgba(244,113,106,.14)', border: '1px solid rgba(244,113,106,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '24px', fontSize: '26px', color: '#f4716a', mx: 'auto' }}>
            ✕
          </Box>
          <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '26px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
            Verification issue
          </Box>
          <Box sx={{ mb: '28px', fontSize: '14.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
            {error}
          </Box>
          <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <AmberButton to="/profile" size="lg">View my account</AmberButton>
            <Box
              component="a"
              href="/support"
              sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#a9bcdc', fontWeight: 500, fontSize: '15px', px: '22px', py: '13px', borderRadius: '10px', border: '1px solid rgba(120,150,200,.28)', transition: 'border-color .15s, color .15s', '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' } }}
            >
              Contact support
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#0a1326', display: 'flex', alignItems: 'center', justifyContent: 'center', px: '28px' }}>
      <Box
        sx={{
          maxWidth: '540px',
          width: '100%',
          textAlign: 'center',
          border: '1px solid rgba(120,150,200,.22)',
          borderRadius: '20px',
          background: 'linear-gradient(180deg,#0e1c39,#0a1326)',
          boxShadow: '0 40px 90px rgba(0,0,0,.5)',
          p: { xs: '36px 24px', sm: '52px 48px' },
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: '24px', display: 'flex', justifyContent: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#44d07b' }} />
        </Box>

        <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: 'clamp(24px,4vw,32px)', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.02em' }}>
          Payment Successful!
        </Box>
        <Box sx={{ mb: '32px', fontSize: '15.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
          Thank you for your subscription. Your account has been activated.
        </Box>

        {subscription && (
          <Box
            sx={{
              mb: '32px',
              textAlign: 'left',
              border: '1px solid rgba(120,150,200,.2)',
              borderRadius: '12px',
              background: 'rgba(10,19,38,.6)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: '18px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.15)', background: 'rgba(8,15,30,.5)' }}>
              <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.12em', color: '#2dd4bf', textTransform: 'uppercase' }}>
                Subscription details
              </Box>
            </Box>
            <Box sx={{ px: '18px', py: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Status', value: subscription.status },
                { label: 'Plan', value: subscription.plan },
                ...(subscription.currentPeriodEnd ? [{
                  label: 'Next billing date',
                  value: new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                }] : []),
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <Box sx={{ fontFamily: MONO, fontSize: '11.5px', color: '#6a82a8', letterSpacing: '.06em' }}>{label}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#dce6fa', textAlign: 'right' }}>{value}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <AmberButton to="/dashboard" size="lg" fullWidth>
          Go to Dashboard <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
        </AmberButton>
      </Box>
    </Box>
  );
};

export default PaymentSuccess;
